import { theme, tokens } from '@cast/design-system';
import { DbProtocolType, TtlMode } from '@cast/types';
import {
  DatabaseInstanceProtocolV2Enum,
  DboAdvisorProtocolType,
  DboRecommendationImpact,
} from '@cast/types-database-optimizer';

import { getRankedTableSx } from 'components/table';

import { DbComponentType } from './_types';

/** Default metrics refetch interval used for light requests. */
export const metricsRefetchInterval = 30_000;

/** Fractional difference between current and projected values to consider a metric optimal */
export const optimalCachingDiffFraction = 0.05;

export const cachePerformanceColors = [
  tokens.theme.data.aqua500,
  tokens.theme.data.yellow200,
  tokens.theme.data.indigo200,
  tokens.theme.data.green200,
  tokens.theme.data.indigo800,
];

export const dboFeatureColors = {
  advisor: tokens.theme.data.indigo400,
  caching: tokens.theme.data.aqua600,
  pooling: tokens.theme.data.blue500,
} as const;

export const cachePerformanceTableSx = getRankedTableSx(
  theme,
  cachePerformanceColors
);

export const isLegacyCriticalImpact = (value: string): boolean =>
  value === 'RECOMMENDATION_IMPACT_CRITICAL';

export const normalizeRecommendationImpact = (
  value: DboRecommendationImpact
): DboRecommendationImpact =>
  isLegacyCriticalImpact(value) ? DboRecommendationImpact.HIGH : value;

export const impactToLabel: Partial<Record<DboRecommendationImpact, string>> = {
  [DboRecommendationImpact.UNSPECIFIED]: 'Unknown',
  [DboRecommendationImpact.HIGH]: 'High',
  [DboRecommendationImpact.MEDIUM]: 'Medium',
  [DboRecommendationImpact.LOW]: 'Low',
};

export const ttlModeToLabel: Record<TtlMode, string> = {
  [TtlMode.Auto]: 'Auto',
  [TtlMode.DontCache]: 'Passthrough',
  [TtlMode.Manual]: 'Manual',
};

export const componentTypeToLabel: Record<DbComponentType, string> = {
  'Manual Component': 'Manual Component',
  Instance: 'Instance',
  Cluster: 'Cluster',
};

export const dbTypeToLabel: Record<
  DbProtocolType | DatabaseInstanceProtocolV2Enum,
  string
> = {
  [DbProtocolType.PostgreSQL]: 'PostgreSQL',
  [DbProtocolType.MySQL]: 'MySQL',
  [DbProtocolType.Oracle]: 'Oracle',
  [DbProtocolType.Unspecified]: 'Unknown',
  [DatabaseInstanceProtocolV2Enum.ORACLE]: 'Oracle',
  [DatabaseInstanceProtocolV2Enum.POSTGRESQL]: 'PostgreSQL',
  [DatabaseInstanceProtocolV2Enum.MYSQL]: 'MySQL',
  [DatabaseInstanceProtocolV2Enum.PROTOCOL_UNSPECIFIED]: 'Unknown',
};

/** Advisor API to DB Optimizer API protocol type map */
export const protocolTypeMap: Record<
  DboAdvisorProtocolType | DatabaseInstanceProtocolV2Enum,
  DbProtocolType
> = {
  [DatabaseInstanceProtocolV2Enum.ORACLE]: DbProtocolType.Oracle,
  [DatabaseInstanceProtocolV2Enum.POSTGRESQL]: DbProtocolType.PostgreSQL,
  [DatabaseInstanceProtocolV2Enum.MYSQL]: DbProtocolType.MySQL,
  [DatabaseInstanceProtocolV2Enum.PROTOCOL_UNSPECIFIED]:
    DbProtocolType.Unspecified,
  [DboAdvisorProtocolType.PROTOCOL_TYPE_UNSPECIFIED]:
    DbProtocolType.Unspecified,
};

export const availableDbTypes = [
  DbProtocolType.PostgreSQL,
  DbProtocolType.MySQL,
  DbProtocolType.Oracle,
] as const;

// Oracle development is stopped and the API does not support it, so Oracle is
// excluded from the selectable database types.
export const supportedDbTypes = availableDbTypes.filter(
  (value) => value !== DbProtocolType.Oracle
);

export type AvailableDbType = (typeof availableDbTypes)[number];

export const defaultPorts: Record<AvailableDbType, number> = {
  [DbProtocolType.PostgreSQL]: 5432,
  [DbProtocolType.MySQL]: 3306,
  [DbProtocolType.Oracle]: 1521,
};
